import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource } from '@opentelemetry/resources';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';

interface TelemetryConfig {
  serviceName: string;
  serviceVersion: string;
  environment: string;
}

/**
 * Initialize OpenTelemetry for trace context propagation.
 *
 * This setup adds W3C trace context headers (traceparent, tracestate) to all
 * outgoing HTTP requests matching the configured domains. No spans are exported
 * to external collectors - this is headers-only propagation for backend correlation.
 *
 * Headers format: traceparent: 00-{trace-id}-{span-id}-01
 *
 * @param config - Service identification and configuration
 */
export function initializeTelemetry(config: TelemetryConfig): void {
  try {
    // Create resource identifying this service
    const resource = new Resource({
      [ATTR_SERVICE_NAME]: config.serviceName,
      [ATTR_SERVICE_VERSION]: config.serviceVersion,
      'deployment.environment': config.environment,
    });

    // Create tracer provider
    const provider = new WebTracerProvider({
      resource,
    });

    // Register provider with context manager
    // This enables trace context to flow through async operations
    provider.register({
      contextManager: new ZoneContextManager(),
    });

    // Register instrumentations
    // This patches fetch() and XMLHttpRequest to automatically inject headers
    registerInstrumentations({
      instrumentations: [
        // Fetch API instrumentation - adds headers to fetch() calls
        new FetchInstrumentation({
          // Only add headers to requests matching these patterns
          propagateTraceHeaderCorsUrls: [
            /^https?:\/\/.*\.spark\.dev/, // *.spark.dev
            /^https?:\/\/spark\.dev/, // spark.dev
            /^https?:\/\/localhost/, // localhost (for dev)
            /^https?:\/\/.*\.bazilli\.com/, // staging domain (if applicable)
          ],

          // Clear timing resources after navigation (browser optimization)
          clearTimingResources: true,
        }),

        // XMLHttpRequest instrumentation - for libraries using XHR instead of fetch
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: [
            /^https?:\/\/.*\.spark\.dev/,
            /^https?:\/\/spark\.dev/,
            /^https?:\/\/localhost/,
            /^https?:\/\/.*\.bazilli\.com/,
          ],
        }),
      ],
    });

    console.info(
      `[Telemetry] Initialized for ${config.serviceName} v${config.serviceVersion}`,
      `\n  Environment: ${config.environment}`,
      `\n  Mode: Headers-only (trace context propagation)`,
      `\n  Headers will be added to API requests matching configured domains`
    );
  } catch (error) {
    console.error('[Telemetry] Failed to initialize:', error);
    // Don't throw - allow app to continue if telemetry fails
  }
}
